import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import { useDraggablePanel } from '../hooks/useDraggablePanel';
import kokoIcon from '../assets/brand/koko.png';
import starIcon from '../assets/brand/Star.svg';
import { applySurveyData, buildDefaultInputPlaceholder, scrollToSurveyField, } from '../lib/smartFill/surveyData';
import { buildAssistantChatMessage, nextChatMessageId } from '../lib/smartFill/buildAssistantMessage';
import { runSmartFill, SmartFillError } from '../services/smartFill';
import { readPersistedSmartFormChat, writePersistedSmartFormChat, } from '../services/smartFormChatCache';
import { WorkflowDebugPanel } from './WorkflowDebugPanel';
function nextMessageId() {
    return nextChatMessageId();
}
function buildUpdatesHeader(fieldCount) {
    const fieldLabel = `field${fieldCount === 1 ? '' : 's'}`;
    return `Updated ${fieldCount} ${fieldLabel}`;
}
const MAX_ADDITIONAL_DETAILS_SHOWN = 20;
const INITIAL_ASSISTANT_PROMPT = "Hi — let's get your form done. You can answer everything in one message or take it a few at a time.";
function buildInitialAssistantMessage() {
    return {
        id: nextMessageId(),
        role: 'assistant',
        userPrompt: null,
        promptIntro: INITIAL_ASSISTANT_PROMPT,
        summary: null,
        missingFields: [],
        ambiguousFields: [],
        additionalDetails: [],
        additionalDetailFields: [],
        requestedDetailFields: [],
        surveyData: {},
        fieldUpdates: [],
    };
}
function CollapsibleBubbleSection({ title, sectionClass, children, }) {
    const [open, setOpen] = useState(false);
    return (_jsxs("div", { className: `bubble-collapsible ${sectionClass ?? ''}`, children: [_jsxs("button", { type: "button", className: "bubble-collapsible-toggle", "aria-expanded": open, onClick: () => setOpen((prev) => !prev), children: [_jsx("span", { className: `bubble-collapsible-chevron${open ? ' is-open' : ''}`, "aria-hidden": "true" }), _jsx("span", { children: title })] }), open && _jsx("div", { className: "bubble-collapsible-body", children: children })] }));
}
function FollowUpSection({ title, fields, sectionClass, badgeClass, badgeLabel, maxItems, onFieldClick, }) {
    if (!fields.length)
        return null;
    const limit = maxItems && maxItems > 0 ? maxItems : fields.length;
    const visibleFields = fields.slice(0, limit);
    const hiddenCount = fields.length - visibleFields.length;
    return (_jsxs(CollapsibleBubbleSection, { title: `${title} (${fields.length})`, sectionClass: `bubble-followups ${sectionClass ?? ''}`.trim(), children: [_jsx("ul", { className: "bubble-followup-list", children: visibleFields.map((field, i) => {
                    const scrollPath = field.path || field.name || '';
                    return (_jsx("li", { className: "bubble-followup-item", children: _jsxs("button", { type: "button", className: "bubble-followup-link", onClick: () => onFieldClick(scrollPath, field.question), children: [_jsx("span", { className: `field-badge ${badgeClass}`, children: badgeLabel }), field.question] }) }, `${scrollPath || field.question}-${i}`));
                }) }), hiddenCount > 0 && (_jsxs("p", { className: "bubble-followups-more", children: ["Showing ", visibleFields.length, " of ", fields.length, ". More details may still be required on the form."] }))] }));
}
function RequestedFieldsSection({ fields, maxItems, onFieldClick, }) {
    if (!fields.length)
        return null;
    const limit = maxItems && maxItems > 0 ? maxItems : fields.length;
    const visibleFields = fields.slice(0, limit);
    const hiddenCount = fields.length - visibleFields.length;
    return (_jsxs(CollapsibleBubbleSection, { title: `Fields to complete (${fields.length})`, sectionClass: "bubble-requested-fields", children: [_jsx("div", { className: "bubble-requested-chips", children: visibleFields.map((field, i) => {
                    const scrollPath = field.path || field.name || '';
                    return (_jsx("button", { type: "button", className: "bubble-requested-chip", onClick: () => onFieldClick(scrollPath, field.question), children: field.question }, `${scrollPath || field.question}-${i}`));
                }) }), hiddenCount > 0 && (_jsxs("p", { className: "bubble-followups-more", children: ["Showing ", visibleFields.length, " of ", fields.length, ". More fields may still be required on the form."] }))] }));
}
function AssistantBubble({ message, onFieldClick, }) {
    const introText = message.promptIntro ||
        (message.userPrompt && !message.requestedDetailFields.length ? message.userPrompt : null) ||
        (!message.userPrompt && message.summary ? message.summary : null);
    const showSummary = Boolean(message.summary && message.summary !== introText);
    const fieldUpdates = message.fieldUpdates || [];
    return (_jsx("div", { className: "chat-bubble-row assistant", children: _jsxs("div", { className: "chat-bubble assistant", children: [introText && _jsx("p", { className: "bubble-prompt", children: introText }), showSummary && _jsx("p", { className: "bubble-summary", children: message.summary }), _jsx(RequestedFieldsSection, { fields: message.requestedDetailFields, maxItems: MAX_ADDITIONAL_DETAILS_SHOWN, onFieldClick: onFieldClick }), _jsx(FollowUpSection, { title: "Still needed", fields: message.missingFields, badgeClass: "required", badgeLabel: "Required", onFieldClick: onFieldClick }), _jsx(FollowUpSection, { title: "Please clarify", fields: message.ambiguousFields, sectionClass: "clarify", badgeClass: "clarify", badgeLabel: "Clarify", onFieldClick: onFieldClick }), !message.requestedDetailFields.length &&
                    !message.missingFields.length &&
                    !message.ambiguousFields.length &&
                    message.additionalDetailFields.length > 0 && (_jsx(FollowUpSection, { title: "Additional details needed", fields: message.additionalDetailFields, maxItems: MAX_ADDITIONAL_DETAILS_SHOWN, badgeClass: "required", badgeLabel: "Ask", onFieldClick: onFieldClick })), fieldUpdates.length > 0 && (_jsx(CollapsibleBubbleSection, { title: buildUpdatesHeader(fieldUpdates.length), sectionClass: "bubble-updates", children: _jsx("div", { className: "bubble-update-chips", children: fieldUpdates.map((update) => (_jsxs("button", { type: "button", className: "bubble-update-chip", onClick: () => onFieldClick(update.path), children: [update.title, ": ", update.displayValue] }, update.path))) }) })), message.debug && _jsx(WorkflowDebugPanel, { debug: message.debug })] }) }));
}
export function SmartFormChat({ survey, formDefinition, surveyContainerRef, persistenceKey, docked = false, onHide, onToggleDock, pendingFillResult, onPendingFillHandled, onContactsFound, }) {
    const [messages, setMessages] = useState(() => {
        const persisted = readPersistedSmartFormChat(persistenceKey);
        return persisted?.messages?.length
            ? persisted.messages
            : [buildInitialAssistantMessage()];
    });
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [conversationHistory, setConversationHistory] = useState(() => readPersistedSmartFormChat(persistenceKey)?.conversationHistory ?? []);
    const [placeholder, setPlaceholder] = useState('Loading form...');
    const messagesEndRef = useRef(null);
    const messagesRef = useRef(messages);
    const conversationHistoryRef = useRef(conversationHistory);
    messagesRef.current = messages;
    conversationHistoryRef.current = conversationHistory;
    const definition = formDefinition;
    useEffect(() => {
        setPlaceholder(buildDefaultInputPlaceholder(definition));
    }, [definition]);
    // Persist chat when this instance unmounts (tab switch / form change).
    useEffect(() => {
        const key = persistenceKey;
        return () => {
            writePersistedSmartFormChat(key, {
                messages: messagesRef.current,
                conversationHistory: conversationHistoryRef.current,
            });
        };
    }, [persistenceKey]);
    const { isListening, statusText, supported, toggleListening, discardListening } = useSpeechRecognition(input, setInput);
    const { panelRef, panelStyle, isDragging, isResizing, onTitlePointerDown, onTitlePointerMove, onTitlePointerUp, onTitlePointerCancel, onResizePointerDown, onResizePointerMove, onResizePointerUp, onResizePointerCancel, } = useDraggablePanel(surveyContainerRef);
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);
    const handleFieldClick = useCallback((path, question) => {
        scrollToSurveyField(survey, path, definition, surveyContainerRef.current, question);
    }, [survey, definition, surveyContainerRef]);
    const applySmartFillResult = useCallback((details, result, loadingMessageId) => {
        const previousData = JSON.parse(JSON.stringify(survey.data));
        try {
            applySurveyData(survey, result.surveyData, definition);
        }
        catch (applyError) {
            setMessages((prev) => [
                ...prev.filter((m) => m.id !== loadingMessageId),
                {
                    id: nextMessageId(),
                    role: 'error',
                    error: `Workflow succeeded but form update failed: ${applyError instanceof Error ? applyError.message : 'unknown error'}`,
                    debug: result.debug,
                },
            ]);
            return false;
        }
        const turn = {
            details,
            surveyData: result.surveyData,
            additionalDetails: result.additionalDetails,
            userPrompt: result.userPrompt,
            summary: result.summary,
            missingFields: result.missingFields,
            ambiguousFields: result.ambiguousFields,
        };
        setConversationHistory((prev) => [...prev, turn]);
        const assistantMessage = buildAssistantChatMessage(result, previousData, definition);
        setMessages((prev) => {
            const withoutLoading = loadingMessageId
                ? prev.filter((m) => m.id !== loadingMessageId)
                : prev;
            const hasUserMessage = details &&
                withoutLoading.some((m) => m.role === 'user' && m.text === details && m.id !== loadingMessageId);
            return [
                ...withoutLoading,
                ...(details && !hasUserMessage
                    ? [{ id: nextMessageId(), role: 'user', text: details }]
                    : []),
                assistantMessage,
            ];
        });
        if (assistantMessage.role === 'assistant' && assistantMessage.promptIntro && !details) {
            setPlaceholder(assistantMessage.promptIntro);
        }
        if (result.contactInformationFound.length > 0) {
            onContactsFound?.(result.contactInformationFound);
        }
        return true;
    }, [survey, definition, onContactsFound]);
    useEffect(() => {
        if (!pendingFillResult)
            return;
        applySmartFillResult(pendingFillResult.userMessage, pendingFillResult.result);
        onPendingFillHandled?.();
    }, [pendingFillResult, applySmartFillResult, onPendingFillHandled]);
    const handleSubmit = useCallback(async () => {
        const details = input.trim();
        const userMessageId = nextMessageId();
        const loadingMessageId = nextMessageId();
        discardListening();
        setMessages((prev) => [
            ...prev,
            { id: userMessageId, role: 'user', text: details },
            { id: loadingMessageId, role: 'loading' },
        ]);
        setInput('');
        setLoading(true);
        console.log('[smart-fill] submit', { details, currentData: survey.data });
        try {
            const result = await runSmartFill({
                details,
                formDefinition: JSON.parse(JSON.stringify(formDefinition)),
                currentData: survey.data,
                conversationHistory,
            });
            console.log('[smart-fill] result', result);
            applySmartFillResult(details, result, loadingMessageId);
        }
        catch (err) {
            console.error('[smart-fill] failed', err);
            setMessages((prev) => [
                ...prev.filter((m) => m.id !== loadingMessageId),
                {
                    id: nextMessageId(),
                    role: 'error',
                    error: err instanceof Error ? err.message : 'Smart fill request failed.',
                    debug: err instanceof SmartFillError ? err.debug : undefined,
                },
            ]);
        }
        finally {
            setInput('');
            setLoading(false);
        }
    }, [
        input,
        discardListening,
        survey,
        formDefinition,
        conversationHistory,
        applySmartFillResult,
    ]);
    return (_jsxs("section", { ref: docked ? undefined : panelRef, className: `smart-form-panel chat-panel${docked ? ' smart-form-panel--docked' : ` smart-form-panel--floating${isDragging ? ' is-dragging' : ''}${isResizing ? ' is-resizing' : ''}`}`, style: docked ? undefined : panelStyle, "aria-labelledby": "chat-title", children: [_jsxs("div", { id: "chat-title", className: "chat-panel-title", role: "heading", "aria-level": 2, onPointerDown: docked ? undefined : onTitlePointerDown, onPointerMove: docked ? undefined : onTitlePointerMove, onPointerUp: docked ? undefined : onTitlePointerUp, onPointerCancel: docked ? undefined : onTitlePointerCancel, children: [_jsx("span", { className: "chat-panel-drag-handle", "aria-hidden": "true" }), _jsx("span", { className: "smart-form-brand-icon-frame", role: "img", "aria-label": "Smart Form", children: _jsxs("span", { className: "smart-form-brand-icon", children: [_jsx("img", { className: "smart-form-brand-owl", src: kokoIcon, alt: "" }), _jsx("img", { className: "smart-form-brand-star smart-form-brand-star--large", src: starIcon, alt: "" }), _jsx("img", { className: "smart-form-brand-star smart-form-brand-star--small", src: starIcon, alt: "" })] }) }), _jsxs("span", { className: "smart-form-title-copy", children: [_jsx("span", { className: "smart-form-title-text", children: "Smart Form Assistant" }), _jsx("span", { className: "smart-form-title-description", children: "Chat to fill in the form. You can also use the fields on the left." })] }), _jsxs("div", { className: "smart-form-controls", onPointerDown: (event) => event.stopPropagation(), children: [onToggleDock && (_jsx("button", { type: "button", className: "smart-form-control", onClick: onToggleDock, children: docked ? 'Float' : 'Dock' })), onHide && (_jsx("button", { type: "button", className: "smart-form-control", onClick: onHide, children: "Hide" }))] })] }), _jsxs("div", { className: "chat-messages", "aria-live": "polite", children: [messages.map((message) => {
                        if (message.role === 'user') {
                            return (_jsx("div", { className: "chat-bubble-row user", children: _jsx("div", { className: "chat-bubble user", children: _jsx("p", { className: "bubble-prompt", children: message.text || _jsx("span", { className: "bubble-empty-start", children: "(Start form)" }) }) }) }, message.id));
                        }
                        if (message.role === 'assistant') {
                            return (_jsx(AssistantBubble, { message: message, onFieldClick: handleFieldClick }, message.id));
                        }
                        if (message.role === 'error') {
                            return (_jsx("div", { className: "chat-bubble-row error", children: _jsxs("div", { className: "chat-bubble error", children: [_jsx("p", { className: "bubble-prompt", children: message.error }), message.debug && _jsx(WorkflowDebugPanel, { debug: message.debug })] }) }, message.id));
                        }
                        if (message.role === 'loading') {
                            return (_jsx("div", { className: "chat-bubble-row loading", children: _jsxs("div", { className: "chat-bubble loading", children: [_jsx("span", { className: "chat-spinner" }), "Thinking..."] }) }, message.id));
                        }
                        return null;
                    }), _jsx("div", { ref: messagesEndRef })] }), _jsxs("div", { className: "chat-input-area", children: [_jsx("label", { htmlFor: "smart-fill-input", className: "visually-hidden", children: "Your message" }), _jsxs("div", { className: "chat-compose-row", children: [_jsx("button", { type: "button", className: `btn btn-secondary microphone-button${isListening ? ' listening' : ''}`, disabled: loading || !supported, onClick: toggleListening, "aria-label": isListening ? 'Stop microphone' : 'Start microphone', title: isListening ? 'Stop microphone' : 'Start microphone', children: _jsxs("svg", { className: "microphone-icon", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", children: [_jsx("path", { d: "M12 15.25a3.75 3.75 0 0 0 3.75-3.75v-5a3.75 3.75 0 0 0-7.5 0v5A3.75 3.75 0 0 0 12 15.25Z", stroke: "currentColor", strokeWidth: "1.8" }), _jsx("path", { d: "M5.75 10.75v.75a6.25 6.25 0 0 0 12.5 0v-.75M12 17.75v3M8.75 20.75h6.5", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round" })] }) }), _jsx("textarea", { id: "smart-fill-input", rows: 2, value: input, placeholder: placeholder, disabled: loading, onChange: (e) => setInput(e.target.value), onKeyDown: (e) => {
                                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                        e.preventDefault();
                                        if (!loading)
                                            void handleSubmit();
                                    }
                                } }), _jsx("button", { type: "button", className: "btn btn-primary submit-icon-button", disabled: loading, onClick: handleSubmit, "aria-label": "Submit and fill form", title: "Submit and fill form", children: _jsxs("svg", { className: "submit-icon", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", children: [_jsx("path", { d: "m4.25 11.25 14.9-6.4-6.4 14.9-1.8-6.7-6.7-1.8Z", stroke: "currentColor", strokeWidth: "1.8", strokeLinejoin: "round" }), _jsx("path", { d: "m10.95 13.05 4.15-4.15", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round" })] }) })] }), statusText && _jsx("p", { className: "status-text", children: statusText })] }), _jsx("button", { type: "button", className: "chat-panel-resize-handle", "aria-label": "Resize panel", onPointerDown: onResizePointerDown, onPointerMove: onResizePointerMove, onPointerUp: onResizePointerUp, onPointerCancel: onResizePointerCancel })] }));
}
